/**
 * useSupport — React Query mutation for the in-app "contact support" form.
 *
 * Endpoint (registered-users only — the backend reads the submitter's email
 * from the session, so the client never sends it):
 *   POST /support/contact   { category?, message }  -> { success: true }
 *
 * The message is delivered to the support inbox with the user's address as
 * reply-to, so a support agent can just hit reply.
 */

import { useMutation } from '@tanstack/react-query';
import { httpClient } from '../http-client';

export interface SupportContactInput {
  /** Optional topic the user picked — folded into the email subject line. */
  category?: string;
  /** The issue description. Backend requires 10–5000 chars after trimming. */
  message: string;
}

/**
 * Submit a support request. On failure the thrown error carries `.status`
 * and (for 4xx) `.code` from the API, so callers can surface a precise
 * message — e.g. `NO_EMAIL_ON_FILE`, `MESSAGE_TOO_SHORT`, `SEND_FAILED`.
 */
export function useSubmitSupportRequest() {
  return useMutation({
    mutationFn: async (input: SupportContactInput) => {
      const body: SupportContactInput = { message: input.message };
      if (input.category && input.category.trim()) {
        body.category = input.category.trim();
      }
      return httpClient.post<{ success: boolean }>('/support/contact', body);
    },
  });
}
